/**
 * knowledgeApi — the Knowledge admin screens (SUN-78) against the FIX 33 contract v1 (SUN-77).
 * Every route is `/dev/knowledge/*` on the Kiaan client: Dev-mode guard on the server, writes need
 * kiaan_manage_knowledge. Answers are `{success, data}`; errors are 400 plain text / 404 / 503.
 */
import { kiaanHttp } from '../kiaanApi';

function unwrap<T>(body: unknown): T {
  const b = body as { data?: unknown } | undefined;
  return (b && typeof b === 'object' && 'data' in b ? (b as { data: T }).data : (body as T));
}

/** A 400 from these routes is a plain sentence meant for the admin ("Type the container name to confirm"). */
export function knowledgeErrorText(err: unknown): string {
  const r = (err as { response?: { status?: number; data?: unknown } } | null)?.response;
  if (r?.status === 403) return 'This login cannot change knowledge. Ask your admin.';
  if (r?.status === 404) return 'That is gone already. Reload the graph.';
  if (r?.status === 400) {
    const d = r.data as { error?: unknown; message?: unknown } | string | undefined;
    const text = typeof d === 'string' ? d : typeof d?.error === 'string' ? d.error : typeof d?.message === 'string' ? d.message : null;
    if (text) return text;
  }
  return 'Knowledge store is not reachable. Nothing was changed.';
}

/** For screen loads: a 404 there means the server part (FIX 33) is not deployed on this site yet. */
export const KNOWLEDGE_NOT_READY = 'The knowledge part of the server is not on this site yet.';
export function knowledgeLoadErrorText(err: unknown): string {
  const status = (err as { response?: { status?: number } } | null)?.response?.status;
  return status === 404 ? KNOWLEDGE_NOT_READY : knowledgeErrorText(err);
}

// ── 2. Graph ───────────────────────────────────────────────────────────────
export type KNodeType = 'root' | 'container' | 'branch' | 'file' | 'memory';

export interface KNode {
  id: string;
  type: KNodeType;
  label: string;
  parentId: string | null;
  pieces?: number;
  files?: number;
  chats?: number;
  agentId?: string;
  importId?: string;
  source?: 'web' | 'desktop';
  kind?: 'STATE' | 'EVENT';
  /** "Kiaan noticed": a newer file on the same topic (optional, set by the server when it knows one). */
  newer?: { id: string; label: string; at?: string } | null;
}

export interface KImport { id: string; at: string; source: 'web' | 'desktop'; files: number }

export interface KGraph {
  nodes: KNode[];
  edges: { from: string; to: string }[];
  lastImport: KImport | null;
  imports: KImport[];
}

export async function getKnowledgeGraph(since?: string | null): Promise<KGraph> {
  const res = await kiaanHttp.get('/dev/knowledge/graph', { params: since ? { since } : undefined, timeout: 15000 });
  const g = unwrap<Partial<KGraph>>(res.data) ?? {};
  return {
    nodes: Array.isArray(g.nodes) ? g.nodes : [],
    edges: Array.isArray(g.edges) ? g.edges : [],
    lastImport: g.lastImport ?? null,
    imports: Array.isArray(g.imports) ? g.imports : [],
  };
}

// ── 1. Containers / branches ───────────────────────────────────────────────
export async function createGroup(label: string, parentKey: string | null = null): Promise<{ key: string; label: string; parentKey: string | null; level: number }> {
  const res = await kiaanHttp.post('/dev/knowledge/groups', { label, parentKey });
  return unwrap(res.data);
}

export async function moveItem(type: 'file' | 'branch', id: string, toGroupKey: string | null): Promise<void> {
  await kiaanHttp.post('/dev/knowledge/move', { type, id, toGroupKey });
}

export async function setGrant(groupKey: string, subjectType: 'role' | 'user' | 'agent', subjectId: string, allow: boolean | null): Promise<void> {
  await kiaanHttp.put('/dev/knowledge/grants', { groupKey, subjectType, subjectId, allow });
}

// ── 3. Exact removal ───────────────────────────────────────────────────────
export type RemoveType = 'file' | 'branch' | 'container';

export interface RemovePreview {
  type: RemoveType;
  label: string;
  files: number;
  branches: number;
  pieces: number;
  agents: { id: string; name: string }[];
  roles: string[];
  people: number;
  willSkipOnSync: number;
}

export async function previewRemove(type: RemoveType, id: string): Promise<RemovePreview> {
  const res = await kiaanHttp.post('/dev/knowledge/remove/preview', { type, id });
  return unwrap(res.data);
}

export async function removeKnowledge(type: RemoveType, id: string, confirmName?: string): Promise<{ removed: { files: number; branches: number; pieces: number; grants: number }; line: string }> {
  const res = await kiaanHttp.post('/dev/knowledge/remove', { type, id, confirm: true, ...(confirmName !== undefined ? { confirmName } : {}) }, { timeout: 30000 });
  return unwrap(res.data);
}

// ── 4. Chat memory ─────────────────────────────────────────────────────────
export interface MemoryChat { threadId: string; title: string; channel: boolean; archived: boolean; messages: number; lastAt: string | null }
export interface AgentMemory { agentId: string; name: string; chatCount: number; messageCount: number; chats: MemoryChat[] }
export interface MemoryMessage { id: string; from: 'user' | 'agent'; name: string; text: string; at: string; removed: boolean }
export type ForgetScope = 'message' | 'chat' | 'agent';
export interface ForgetBody { scope: ForgetScope; agentId: string; threadId?: string; messageId?: string }
export interface ForgetPreview { scope: ForgetScope; agentName: string; chats: number; messages: number; willResetSessions: number }

export async function listMemory(): Promise<AgentMemory[]> {
  const res = await kiaanHttp.get('/dev/knowledge/memory', { timeout: 15000 });
  const d = unwrap<AgentMemory[]>(res.data);
  return Array.isArray(d) ? d : [];
}

export async function getMemoryMessages(agentId: string, threadId: string): Promise<MemoryMessage[]> {
  const res = await kiaanHttp.get(`/dev/knowledge/memory/${encodeURIComponent(agentId)}/threads/${encodeURIComponent(threadId)}/messages`, { timeout: 15000 });
  const d = unwrap<MemoryMessage[]>(res.data);
  return Array.isArray(d) ? d : [];
}

export async function previewForget(body: ForgetBody): Promise<ForgetPreview> {
  const res = await kiaanHttp.post('/dev/knowledge/memory/preview', body);
  return unwrap(res.data);
}

export async function forgetMemory(body: ForgetBody): Promise<{ redacted: number; chatsArchived: number; sessionsReset: number }> {
  const res = await kiaanHttp.post('/dev/knowledge/memory/forget', { ...body, confirm: true }, { timeout: 30000 });
  return unwrap(res.data);
}

// ── 5. Per-agent / per-role access ─────────────────────────────────────────
export type AccessState = 'on' | 'partial' | 'off';

export interface AgentAccessNode {
  key: string;
  label: string;
  parentKey: string | null;
  state: AccessState;
  own: boolean | null;
  inherited: boolean;
  children: AgentAccessNode[];
}

export async function getAgentAccess(agentId: string): Promise<AgentAccessNode[]> {
  const res = await kiaanHttp.get(`/dev/knowledge/access/agent/${encodeURIComponent(agentId)}`);
  const d = unwrap<{ tree?: AgentAccessNode[] }>(res.data);
  return Array.isArray(d?.tree) ? d.tree : [];
}

export async function setAgentAccess(agentId: string, groupKey: string, allow: boolean | null): Promise<void> {
  await kiaanHttp.put(`/dev/knowledge/access/agent/${encodeURIComponent(agentId)}`, { groupKey, allow });
}

export interface RoleCell { state: AccessState; own: boolean | null }
export interface RoleAccessGroup { key: string; label: string; parentKey: string | null; level: number; cells: Record<string, RoleCell> }
export interface RoleAccess { roles: string[]; groups: RoleAccessGroup[] }

export async function getRoleAccess(): Promise<RoleAccess> {
  const res = await kiaanHttp.get('/dev/knowledge/access/roles');
  const d = unwrap<Partial<RoleAccess>>(res.data) ?? {};
  return { roles: Array.isArray(d.roles) ? d.roles : [], groups: Array.isArray(d.groups) ? d.groups : [] };
}

// ── 6. Rejected (SUN-89) ───────────────────────────────────────────────────
/** A file the automatic knowledge check refused. The text was never stored; only this line is kept. */
export interface RejectedItem { at: string; line: string; file: string; container: string; reasons: string[]; notes: string[] }

export async function listRejected(): Promise<RejectedItem[]> {
  const res = await kiaanHttp.get('/dev/knowledge/rejected', { timeout: 15000 });
  const d = unwrap<RejectedItem[]>(res.data);
  return Array.isArray(d) ? d : [];
}
